import React, {
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DirectionsCar } from '@material-ui/icons';
import {
  Container,
  Box,
  Button,
  Dialog,
  DialogActions,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import images from 'src/assets/payment';
import { Layout } from 'src/layouts';
import {
  confirmKlarnaAppointment,
  createKlarnaIntent,
  updateCard,
} from 'src/api/quote';
import { QuoteContext } from 'src/views/Quote/QuoteContext';
import { Image } from 'src/components/atoms';
import {
  CardOnFile,
  IAppointment,
  QuoteShowModal,
  ResponseAppointment,
} from 'src/types';
import callApi from 'src/api/request';
import { IReduxState } from 'src/store/reducers';
import KlarnaPromo from 'src/components/atoms/KlarnaPromo';
import KlarnaSteps from 'src/components/atoms/KlarnaSteps';
import { setAppointment } from 'src/store/actions';
import { callAdsBooking } from 'src/utils/ads';
import { cardTypes } from 'src/utils/data';
import useStyles from './styles';
import useFontStyles from '../../fontStyles';
import CheckoutForm from '../ModalFinishBooking/CheckoutForm';

interface ModalHandleEstimateResponseProps {
  show: boolean;
  onClose: () => void;
  payVerified: boolean | undefined;
}

const ModalHandleEstimateResponse = (
  props: ModalHandleEstimateResponseProps
): ReactElement => {
  const { show, onClose, payVerified } = props;
  const theme = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const fontClasses = useFontStyles();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [errors, setErrors] = useState<string | undefined | null>(null);
  const [stripeRequestInProgress, setStripeRequestInProgress] = useState(false);
  const [isCardOnFile, setIsCardOnFile] = useState<boolean>(false);
  const [lastFour, setLastFour] = useState<string>('');
  const [cardBrand, setCardBrand] = useState<string>('');
  const [prevCardSelected, setPrevCardSelected] = useState(false);
  const [errorCardOnFile, setErrorCardOnFile] = useState<
    string | undefined | null
  >(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string>('card');
  const [klarnaError, setKlarnaError] = useState<string | undefined>();
  const [klarnaInitializing, setKlarnaInitializing] = useState(false);
  const [klarnaRequestInProgress, setKlarnaRequestInProgress] = useState(false);
  const klarnaIntentRequestedFor = useRef<string | null>(null);
  const klarnaReturnHandled = useRef(false);

  const appointment: IAppointment | null = useSelector(
    (state: IReduxState) => state.quote.appointment
  );
  const { handleShowModal, handleRespondAppointmentEstimate } =
    useContext(QuoteContext);

  const showKlarnaOption =
    Boolean(appointment?.attributes?.estimate) &&
    paymentTotal > 0 &&
    !appointment?.attributes?.fleet_job;

  const handleCheckboxChange = () => {
    setPrevCardSelected(!prevCardSelected);
  };

  useEffect(() => {
    const userId = appointment?.attributes.user_id;

    if (userId) {
      callApi<CardOnFile>({
        url: `/api/v2/user/card_on_file/${userId}`,
        method: 'GET',
      }).then((response) => {
        if (response.errors && response.errors.length > 0) {
          setErrorCardOnFile(
            'An error occurred while processing your request.'
          );
        } else if (
          response.card_on_file?.card_brand &&
          response.card_on_file?.last_4_digits
        ) {
          setIsCardOnFile(true);
          setLastFour(response.card_on_file?.last_4_digits as string);
          setCardBrand(response.card_on_file?.card_brand);
        }
      });
    }
  }, [appointment?.attributes.user_id]);

  useEffect(() => {
    if (appointment) {
      const fleet = appointment.attributes.fleet_job;

      if (!fleet) {
        if (appointment?.attributes?.member_job === true) {
          setPaymentTotal(
            appointment?.attributes.estimate?.total_member_price || 0
          );
        } else {
          setPaymentTotal(appointment?.attributes.estimate?.total_price || 0);
        }
      }
    }

    if (payVerified) {
      setPaymentVerified(true);
      handleShowModal(QuoteShowModal.SCHEDULE_SERVICE);
    }
  }, [appointment, handleShowModal, payVerified]);

  const clearKlarnaParams = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('payment_intent');
    url.searchParams.delete('payment_intent_client_secret');
    url.searchParams.delete('redirect_status');
    window.history.replaceState({}, document.title, url.toString());
  };

  useEffect(() => {
    if (!appointment?.id || !showKlarnaOption) {
      return;
    }

    if (appointment.attributes.payment_intent_secret) {
      return;
    }

    const requestKey = `${appointment.id}:${paymentTotal}`;
    if (klarnaIntentRequestedFor.current === requestKey) {
      return;
    }

    klarnaIntentRequestedFor.current = requestKey;
    setKlarnaInitializing(true);

    createKlarnaIntent(appointment.id)
      .then((response) => {
        if (response.errors) {
          setKlarnaError(response.errors.join('. '));
          klarnaIntentRequestedFor.current = null;
          return;
        }

        dispatch(setAppointment(response.data));
        setKlarnaError(undefined);
      })
      .catch(() => {
        setKlarnaError('There was an error preparing Klarna.');
        klarnaIntentRequestedFor.current = null;
      })
      .finally(() => {
        setKlarnaInitializing(false);
      });
  }, [
    appointment?.id,
    appointment?.attributes?.payment_intent_secret,
    dispatch,
    paymentTotal,
    showKlarnaOption,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectStatus = params.get('redirect_status');
    const paymentIntentId = params.get('payment_intent');

    if (
      !appointment?.id ||
      !redirectStatus ||
      !paymentIntentId ||
      klarnaReturnHandled.current
    ) {
      return;
    }

    if (redirectStatus !== 'succeeded') {
      setKlarnaError('Klarna authorization was not completed.');
      clearKlarnaParams();
      klarnaReturnHandled.current = true;
      return;
    }

    klarnaReturnHandled.current = true;
    setKlarnaRequestInProgress(true);
    setKlarnaError(undefined);

    confirmKlarnaAppointment(appointment.id, paymentIntentId)
      .then((resp: ResponseAppointment) => {
        if (resp.errors) {
          setKlarnaError(resp.errors.join('. '));
          return;
        }

        dispatch(setAppointment(resp.data));
        callAdsBooking(resp.data);
        clearKlarnaParams();
        setPaymentVerified(true);
        handleShowModal(QuoteShowModal.SCHEDULE_SERVICE);
      })
      .catch(() => {
        setKlarnaError('There was an error finalizing your Klarna payment.');
        clearKlarnaParams();
      })
      .finally(() => {
        setKlarnaRequestInProgress(false);
      });
  }, [appointment?.id, dispatch, handleShowModal]);

  const handleKlarnaCheckout = async () => {
    if (!appointment?.id || !stripe) {
      setKlarnaError('Payment form is still loading. Please try again.');
      return;
    }

    setKlarnaRequestInProgress(true);
    setKlarnaError(undefined);

    try {
      let clientSecret = appointment.attributes.payment_intent_secret;

      if (!clientSecret) {
        const response = await createKlarnaIntent(appointment.id);

        if (response.errors) {
          setKlarnaError(response.errors.join('. '));
          return;
        }

        dispatch(setAppointment(response.data));
        clientSecret = response.data.attributes.payment_intent_secret;
      }

      if (!clientSecret) {
        setKlarnaError('Unable to initialize Klarna.');
        return;
      }

      const returnUrl = new URL(
        `${window.location.origin}${window.location.pathname}`
      );
      returnUrl.searchParams.set('schedule_estimate', '1');

      const result = await (
        stripe as unknown as {
          confirmKlarnaPayment: (
            secret: string,
            data: {
              payment_method: {
                billing_details: {
                  email?: string;
                  name?: string;
                  address: {
                    country: string;
                    postal_code?: string;
                    line1?: string;
                  };
                };
              };
              return_url: string;
            }
          ) => Promise<{
            error?: { message?: string };
            paymentIntent?: { id: string; status?: string };
          }>;
        }
      ).confirmKlarnaPayment(clientSecret, {
        payment_method: {
          billing_details: {
            email: appointment.attributes.customer_email,
            name: appointment.attributes.customer_name,
            address: {
              country: 'US',
              postal_code: appointment.attributes.address,
              line1: appointment.attributes.exact_address,
            },
          },
        },
        return_url: returnUrl.toString(),
      });

      if (result?.error?.message) {
        setKlarnaError(result.error.message);
        return;
      }

      if (
        result?.paymentIntent?.id &&
        ['requires_capture', 'succeeded'].includes(
          result.paymentIntent.status || ''
        )
      ) {
        const finalizedResponse = await confirmKlarnaAppointment(
          appointment.id,
          result.paymentIntent.id
        );

        if (finalizedResponse.errors) {
          setKlarnaError(finalizedResponse.errors.join('. '));
          return;
        }

        dispatch(setAppointment(finalizedResponse.data));
        callAdsBooking(finalizedResponse.data);
        setPaymentVerified(true);
        handleShowModal(QuoteShowModal.SCHEDULE_SERVICE);
      }
    } catch (error) {
      setKlarnaError('There was an error starting Klarna. Please try again.');
    } finally {
      setKlarnaRequestInProgress(false);
    }
  };

  const classes = useStyles();

  if (!appointment || !appointment?.attributes || !appointment?.id) {
    return <></>;
  }

  const { car } = appointment.attributes;

  const handleCheckOut = async () => {
    setStripeRequestInProgress(true);
    setErrors(null);

    if (!stripe || !elements) {
      setStripeRequestInProgress(false);
      return false;
    }

    const cardElem = elements.getElement(CardElement);

    if (!cardElem) {
      setStripeRequestInProgress(false);
      return false;
    }

    const { token } = await stripe.createToken(cardElem);

    if (token) {
      updateCard(appointment.id, token.id)
        .then((resp: ResponseAppointment) => {
          if (resp.errors) {
            setErrors(resp.errors.join('. '));
          } else {
            setPaymentVerified(true);
            handleShowModal(QuoteShowModal.SCHEDULE_SERVICE);
          }
        })
        .finally(() => setStripeRequestInProgress(false));
    } else {
      setStripeRequestInProgress(false);
      setErrors('Error updating Credit Card');
    }

    return true;
  };

  const paymentMethodSection = (
    <>
      {showKlarnaOption && (
        <>
          <KlarnaSteps ctaText="Schedule" />
          <KlarnaPromo amount={paymentTotal} />
        </>
      )}
      {showKlarnaOption && (
        <RadioGroup
          value={selectedPaymentMethod}
          onChange={(event) => {
            setKlarnaError(undefined);
            setSelectedPaymentMethod(event.target.value);
          }}
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 20,
            justifyContent: 'center',
            marginTop: 12,
          }}
        >
          <FormControlLabel
            value="card"
            control={<Radio style={{ color: '#005959' }} />}
            label={
              <Typography style={{ fontFamily: 'Inter' }}>Card</Typography>
            }
          />
          <FormControlLabel
            value="klarna"
            control={<Radio style={{ color: '#005959' }} />}
            label={
              <Typography style={{ fontFamily: 'Inter' }}>Klarna</Typography>
            }
          />
        </RadioGroup>
      )}

      {(selectedPaymentMethod !== 'klarna' || !showKlarnaOption) && (
        <>
          <CheckoutForm errors={errors} />
          <DialogActions className={classes.actionContainer}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.reservationButton}
              onClick={handleCheckOut}
              disabled={stripeRequestInProgress || klarnaRequestInProgress}
            >
              Schedule
              <Image src={images.arrowRight} />
            </Button>
            {errors && <Typography color="error">{errors}</Typography>}
          </DialogActions>
        </>
      )}

      {showKlarnaOption && selectedPaymentMethod === 'klarna' && (
        <Box style={{ marginTop: 16 }}>
          <Typography style={{ fontFamily: 'Inter', marginBottom: 8 }}>
            Continue with Klarna to authorize your payment now. You won&apos;t
            be charged until the day of service.
          </Typography>
          {klarnaInitializing && (
            <Typography style={{ marginTop: 8, fontFamily: 'Inter' }}>
              Preparing Klarna...
            </Typography>
          )}
          {klarnaError && (
            <Typography
              color="error"
              style={{ marginTop: 8, fontFamily: 'Inter' }}
            >
              {klarnaError}
            </Typography>
          )}
          <DialogActions className={classes.actionContainer}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.reservationButton}
              onClick={handleKlarnaCheckout}
              disabled={
                klarnaInitializing ||
                klarnaRequestInProgress ||
                stripeRequestInProgress ||
                !appointment?.attributes.payment_intent_secret
              }
            >
              Continue with Klarna
              <Image src={images.arrowRight} />
            </Button>
          </DialogActions>
        </Box>
      )}
    </>
  );

  return (
    <Dialog
      open={show}
      onClose={onClose}
      aria-labelledby="responsive-dialog-title"
      scroll="body"
      fullScreen
    >
      <Layout>
        <Container
          maxWidth="lg"
          className={classes.root}
          style={{
            background: '#ffffff',
            display: 'flex',
            flexDirection: isSm ? 'column' : 'row',
          }}
        >
          <div>
            <Card
              style={{
                flex: 1,
                minWidth: !isSm ? 700 : 300,
                padding: isSm ? '20px' : '30px 60px',
                background: '#FFF',
                boxShadow: isSm
                  ? 'none'
                  : 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;',

                borderRadius: 12,
                marginTop: 50,
              }}
              elevation={4}
            >
              <Box
                key="image-payments"
                display="flex"
                flexDirection="row"
                alignItems="center"
                gridGap={10}
                marginTop={2}
                style={{ marginBottom: 30 }}
              >
                {cardTypes.map((ct) => (
                  <Image key={ct.title} src={ct.image} />
                ))}
              </Box>
              <Typography
                style={{
                  fontSize: isSm ? 15 : 30,
                  fontFamily: 'Inter',
                  fontWeight: 700,
                }}
              >
                {paymentVerified ? 'Schedule Appointment' : 'Choose Payment'}
              </Typography>

              <Box style={{ marginTop: '20px' }}>
                <Box style={{ margin: '20px auto' }}>
                  {appointment.attributes?.payment_type === 'stripe' &&
                  appointment.attributes?.credit_card_present ? (
                    <Box style={{ textAlign: 'center', marginTop: '5px' }}>
                      {isCardOnFile && (
                        <Card className={classes.cardOnFile} elevation={0}>
                          <Typography
                            style={{ fontFamily: 'Inter', fontWeight: 600 }}
                          >
                            Use card on file
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={prevCardSelected}
                                onChange={() => {
                                  setPaymentVerified(true);
                                  handleCheckboxChange();
                                }}
                                color="primary"
                              />
                            }
                            label={`${cardBrand} ending ${lastFour}`}
                          />
                        </Card>
                      )}

                      {prevCardSelected ? (
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          className={classes.reservationButton}
                          onClick={() => {
                            setPaymentVerified(true);
                            handleShowModal(QuoteShowModal.SCHEDULE_SERVICE);
                          }}
                          disabled={false}
                        >
                          Schedule
                          <Image src={images.arrowRight} />
                        </Button>
                      ) : (
                        paymentMethodSection
                      )}
                    </Box>
                  ) : (
                    <Box style={{ textAlign: 'center', marginTop: '5px' }}>
                      {paymentMethodSection}
                    </Box>
                  )}
                </Box>
              </Box>
            </Card>
          </div>
          <div>
            <Card
              style={{
                padding: 25,
                marginTop: 50,
                background: '#FFF',
                boxShadow: isSm
                  ? 'none'
                  : 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;',
                borderRadius: 12,
              }}
              elevation={3}
            >
              <Typography
                style={{ fontSize: 20, fontFamily: 'Inter', fontWeight: 700 }}
              >
                Summary
              </Typography>

              <div
                style={{
                  display: 'flex',
                  gap: 15,
                  marginTop: 20,
                  alignItems: 'flex-start',
                }}
              >
                <DirectionsCar style={{ color: '#005959' }} />

                <Typography
                  style={{ fontSize: 15, fontFamily: 'Inter', fontWeight: 500 }}
                >
                  {' '}
                  {car.year} {car.make}
                </Typography>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 15,
                  marginTop: 20,
                  alignItems: 'flex-start',
                }}
              >
                <Image src={images.coin} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography className={classes.thickInter}>
                    {' '}
                    {appointment.attributes?.member_job
                      ? appointment?.attributes?.estimate?.total_member_price
                      : appointment?.attributes?.estimate?.total_price}
                  </Typography>
                  <Typography className={classes.thinInter}>
                    {' '}
                    to be charged at time of service
                  </Typography>
                </div>
              </div>
              <p
                className={fontClasses.p1}
                style={{ color: '#474747', marginTop: 30 }}
              >
                <span className={fontClasses.h3} style={{ marginRight: '4px' }}>
                  🎖
                </span>{' '}
                24 month / 24,000 Mile Service Warranty
              </p>
            </Card>
          </div>
        </Container>
      </Layout>
    </Dialog>
  );
};

export default ModalHandleEstimateResponse;
